import type { Experiment, Variant } from 'types';
import { useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import {
  DESTINATION_REQUIRED_MESSAGE,
  isOriginalVariantName,
  ORIGINAL_NAME_TAKEN,
  redirectUrlProblem,
} from 'helpers/variant-fields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useVariants } from '@/state/experiments/useVariants';
import { apiErrorMessage } from '@/state/api-error';

type EditVariantModalProps = {
  variant: Variant;
  experiment: Experiment;
  open: boolean;
  onClose: () => void;
};

const FIELD_CLASS = 'border-line bg-field text-foreground rounded-lg';

/**
 * Name, and on a URL Redirect experiment the destination.
 *
 * It was Rename Variant, which had no destination field and sent the stored one
 * back unchanged, so a redirect's address could not be corrected after creation
 * at all. On a running experiment the change goes live as soon as it is saved
 * (the API rebuilds the site's snippet), which is why that case says what it does
 * to the results before the button is pressed.
 */
export const EditVariantModal = ({ variant, experiment, open, onClose }: EditVariantModalProps) => {
  const { updateVariant } = useVariants(experiment.id);
  const [name, setName] = useState(variant.name);
  const [redirectUrl, setRedirectUrl] = useState(variant.redirectUrl ?? '');
  const [urlTouched, setUrlTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();

  const isUrlRedirect = experiment.type === 'URL Redirect';
  const isLive = experiment.status === 'Running' || experiment.status === 'Deployed';
  // An ended experiment's results describe the destination it ran with, so that stays fixed.
  const urlEditable = isUrlRedirect && experiment.status !== 'Ended';
  const trimmedName = name.trim();
  const trimmedUrl = redirectUrl.trim();
  const previousUrl = variant.redirectUrl ?? '';

  const nameChanged = trimmedName !== variant.name;
  const urlChanged = urlEditable && trimmedUrl !== previousUrl;

  const nameProblem = !trimmedName
    ? 'Give the variant a name'
    : isOriginalVariantName(trimmedName)
      ? ORIGINAL_NAME_TAKEN
      : undefined;
  // Only once the destination is edited: a legacy value saved before validation
  // must not block a rename, which on a running test would force a live URL change.
  const urlProblem = !urlChanged
    ? undefined
    : !trimmedUrl
      ? DESTINATION_REQUIRED_MESSAGE
      : redirectUrlProblem(trimmedUrl);

  const canSave = (nameChanged || urlChanged) && !nameProblem && !urlProblem && !isSaving;
  // A deployed experiment serves only its deployed variant, so editing the others changes nothing visitors see.
  const servesThisVariant = experiment.status === 'Running' || (isLive && !!variant.deployed);
  const warnsLive = servesThisVariant && urlChanged;

  const onSave = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    setUrlTouched(true);
    if (!canSave) return;

    setIsSaving(true);
    setSaveError(undefined);
    try {
      await updateVariant({
        id: variant.id,
        name: nameChanged ? trimmedName : undefined,
        redirectUrl: urlChanged ? trimmedUrl : undefined,
      });
      onClose();
    } catch (error) {
      // Stays open with what was typed, and says why: the API's reason is the
      // useful one (a destination it refused, a name already taken).
      setSaveError(apiErrorMessage(error, 'Could not save the variant.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="border-line bg-panel sm:rounded-panel sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-title font-medium">Edit Variant</DialogTitle>
        </DialogHeader>

        <form id="edit-variant-form" onSubmit={onSave} className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <Label htmlFor="variant-name" className="micro-label">
              Name
            </Label>
            <Input
              id="variant-name"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="Variant name"
              aria-invalid={nameChanged && !!nameProblem}
              aria-describedby={nameChanged && nameProblem ? 'variant-name-error' : undefined}
              className={FIELD_CLASS}
            />
            {nameChanged && nameProblem && (
              <p id="variant-name-error" className="text-danger text-meta">
                {nameProblem}
              </p>
            )}
          </div>

          {isUrlRedirect && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="variant-redirect-url" className="micro-label">
                Destination URL
              </Label>
              <Input
                id="variant-redirect-url"
                inputMode="url"
                value={redirectUrl}
                onChange={(ev) => setRedirectUrl(ev.target.value)}
                onBlur={() => setUrlTouched(true)}
                readOnly={!urlEditable}
                placeholder="https://example.com/destination"
                aria-invalid={urlTouched && !!urlProblem}
                aria-describedby={urlTouched && urlProblem ? 'variant-redirect-url-error' : undefined}
                className={`${FIELD_CLASS} font-mono text-meta`}
              />
              {!urlEditable ? (
                <p className="supporting">This experiment has ended, so its destination stays as it ran.</p>
              ) : urlTouched && urlProblem ? (
                <p id="variant-redirect-url-error" className="text-danger text-meta">
                  {urlProblem}
                </p>
              ) : warnsLive && previousUrl ? (
                <p className="supporting">
                  Was <span className="text-foreground font-mono break-all">{previousUrl}</span>
                </p>
              ) : (
                <p className="supporting">
                  Visitors in this variant are sent here when they land on a page that matches URL Targeting.
                </p>
              )}
            </div>
          )}

          {warnsLive && (
            <div role="note" className="border-warn-line bg-warn-bg rounded-card flex gap-2.5 border p-3">
              <TriangleAlert className="text-warn mt-0.5 size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <div className="flex flex-col gap-1">
                <p className="text-warn text-meta font-medium">
                  {experiment.status === 'Deployed' ? 'This experiment is deployed' : 'This experiment is running'}
                </p>
                <p className="text-foreground text-meta">
                  {experiment.status === 'Deployed'
                    ? 'The new address goes live on your site as soon as you save.'
                    : 'The new address goes live on your site as soon as you save. Visitors already in this variant keep their assignment, so results will mix both destinations. Change it only to fix a broken or wrong address.'}
                </p>
              </div>
            </div>
          )}

          {saveError && (
            <p role="alert" className="text-danger text-meta">
              {saveError}
            </p>
          )}
        </form>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
          >
            Cancel
          </Button>
          {/* Submits the form, so Enter in either field saves; outside it, Enter only
              worked when the dialog had a single field. */}
          <Button
            type="submit"
            form="edit-variant-form"
            disabled={!canSave}
            className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white"
          >
            {isSaving ? 'Saving...' : warnsLive ? 'Change destination' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
